import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import "./styles/fonts.css";
import "./styles/tailwind.css";
import "./styles/index.css";
import "./styles/patient-mobile.css";
import { installChunkReload } from "./lib/chunkReload";

installChunkReload();

// Unregister any old service workers that may be serving stale content
if ('serviceWorker' in navigator) {
  navigator.serviceWorker?.getRegistrations()?.then((registrations) => {
    registrations?.forEach((registration) => {
      registration?.unregister();
    });
  });
  // Also clear all caches
  if ('caches' in window) {
    caches.keys()?.then((cacheNames) => {
      cacheNames?.forEach((cacheName) => {
        caches.delete(cacheName);
      });
    });
  }
}

// ── Root-level hydration timeout guard ──────────────────────────────────────
// If #root is still empty after 10 seconds (e.g. chunk load failure, network
// timeout, or React hydration error), replace the blank screen with a
// user-friendly recovery UI.
const HYDRATION_TIMEOUT_MS = 10000;

const recoveryHTML = `
  <div style="
    min-height:100vh;
    display:flex;
    align-items:center;
    justify-content:center;
    background:#0f0f1a;
    font-family:system-ui,-apple-system,sans-serif;
  ">
    <div style="
      text-align:center;
      padding:2.5rem;
      max-width:420px;
      background:rgba(255,255,255,0.04);
      border:1px solid rgba(139,92,246,0.25);
      border-radius:16px;
      box-shadow:0 8px 32px rgba(0,0,0,0.4);
    ">
      <div style="margin-bottom:1.25rem">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
          fill="none" stroke="#8b5cf6" stroke-width="1.5"
          stroke-linecap="round" stroke-linejoin="round"
          style="margin:0 auto;display:block">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      </div>
      <h1 style="font-size:1.375rem;font-weight:600;color:#f1f5f9;margin-bottom:0.625rem">
        Taking longer than expected to load
      </h1>
      <p style="font-size:0.9375rem;color:#94a3b8;line-height:1.6;margin-bottom:1.75rem">
        The page is taking too long to load. This may be due to a slow connection or a temporary issue.
      </p>
      <button
        onClick="window.location.reload()"
        style="
          display:inline-flex;align-items:center;gap:0.5rem;
          background:linear-gradient(135deg,#7c3aed,#6d28d9);
          color:#fff;border:none;border-radius:8px;
          padding:0.625rem 1.5rem;font-size:0.9375rem;font-weight:600;
          cursor:pointer;box-shadow:0 4px 14px rgba(124,58,237,0.4);
        "
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round">
          <polyline points="23 4 23 10 17 10"/>
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
        </svg>
        Reload Page
      </button>
      <div style="margin-top:1rem">
        <a href="/" style="font-size:0.875rem;color:#8b5cf6;text-decoration:none">
          ← Return to Home
        </a>
      </div>
    </div>
  </div>
`;

const container = document.getElementById("root");
// Keep the static crawler/pre-rendered HTML so the error boundary can fall back to it on public pages.
try {
  window.__CG_STATIC_HTML = (container && container.querySelector('[data-static]') ? container.innerHTML : '')
    || document.getElementById('prerender-snapshot')?.innerHTML || '';
} catch { window.__CG_STATIC_HTML = ''; }

// Start hydration watchdog before React mounts
const hydrationWatchdog = setTimeout(() => {
  // If #root has no meaningful child content, React failed to hydrate
  if (container && (!container?.firstChild || container?.innerHTML?.trim() === '')) {
    container.innerHTML = recoveryHTML;
  }
}, HYDRATION_TIMEOUT_MS);

// Mount React
const root = createRoot(container);
// Outer boundary: a crash above the route-level boundary (e.g. in a context provider) shows the
// static page copy instead of a blank screen.
root.render(<ErrorBoundary><App /></ErrorBoundary>);

// React mounted successfully — clear the watchdog
// We use a short rAF loop to detect when React has actually painted content
const clearWatchdog = () => {
  if (container && container?.firstChild) {
    clearTimeout(hydrationWatchdog);
  } else {
    requestAnimationFrame(clearWatchdog);
  }
};
requestAnimationFrame(clearWatchdog);
